import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { HasMark, CompanyName } from "@/components/BrandText";
import { COMPANY, IMAGES, WHY_CHOOSE } from "@/data/site";

export default function About() {
  return (
    <div data-testid="page-about" className="pt-32">
      {/* HERO HEADER */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-20 md:pt-20 md:pb-28">
          <Reveal>
            <div className="overline mb-5">About Us</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl md:text-7xl text-white leading-[1.05] tracking-tight max-w-4xl">
              Building Sustainable Growth Through <span className="italic gold-gradient-text">Strategy & Intelligence</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-3xl text-white/70 text-lg leading-relaxed">
              <CompanyName /> is a Dubai-based consulting firm dedicated to helping organizations improve performance,
              strengthen market positioning, and unlock new opportunities.
            </p>
          </Reveal>
        </div>
      </section>

      {/* IMAGE + TEXT */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-24 h-24 border-l border-t border-[#D4AF37]/60" />
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r border-b border-[#D4AF37]/60" />
              <img src={IMAGES.boardroom} alt="Executive boardroom" className="w-full h-[440px] md:h-[560px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <h3 className="font-display text-3xl text-white leading-snug">A Different Kind of Consulting Firm.</h3>
            <p className="mt-5 text-white/70 leading-relaxed">
              Our approach combines strategic thinking, market intelligence, industry expertise, and practical execution
              to help businesses achieve measurable results and long-term success.
            </p>
            <p className="mt-4 text-white/60 leading-relaxed">
              We work at the intersection of strategy and execution — translating research and analysis into clear,
              actionable plans that move organisations forward.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Independent, research-driven advisory",
                "Dubai-headquartered, internationally minded",
                "Senior-led engagement on every mandate",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/80">
                  <span className="mt-1 h-px w-6 bg-[#D4AF37]" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/contact" className="btn-gold" data-testid="about-cta">
                Speak With Our Team <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative py-24 md:py-32 bg-[#0c0c0c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="lux-card gold-brackets p-10 h-full">
              <div className="overline mb-5">Mission</div>
              <p className="font-display text-2xl md:text-3xl text-white leading-snug">
                To empower businesses with strategic insights, market intelligence, and growth solutions that create
                <span className="italic gold-gradient-text"> long-term value.</span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="lux-card gold-brackets p-10 h-full">
              <div className="overline mb-5">Vision</div>
              <p className="font-display text-2xl md:text-3xl text-white leading-snug">
                To become a trusted consulting partner for organizations seeking growth, innovation, and
                <span className="italic gold-gradient-text"> market leadership</span> across regional and global markets.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel
            overline={<>Why <HasMark /></>}
            title="What Makes Our Counsel Different"
            subtitle="Five principles that shape every engagement, every recommendation, and every long-term partnership."
            className="mb-14"
          />
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {WHY_CHOOSE.map((w) => {
              const Icon = w.icon;
              return (
                <Item key={w.title}>
                  <div className="lux-card gold-brackets p-6 h-full">
                    <div className="h-12 w-12 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h4 className="font-display text-xl text-white mt-5">{w.title}</h4>
                    <p className="mt-2 text-sm text-white/65 leading-relaxed">{w.desc}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
